import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isTransparent = isHomePage && !scrolled;
  const navBg = isTransparent ? "bg-transparent" : "bg-background-50/95 backdrop-blur-md";
  const textColor = isTransparent ? "text-white" : "text-foreground-950";
  const textMuted = isTransparent ? "text-white/80 hover:text-white" : "text-foreground-700 hover:text-foreground-950";
  const dropdownBtnBg = isTransparent ? "bg-white/15 text-white hover:bg-white/25" : "bg-background-100 text-foreground-700 hover:bg-background-200";
  const hamburgerColor = isTransparent ? "text-white hover:bg-white/10" : "text-foreground-700 hover:bg-background-100";

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${navBg} ${!isTransparent ? "shadow-sm" : ""}`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
        <div className="flex items-center gap-4 md:gap-6">
          <Link
            to="/"
            className={`font-heading text-lg md:text-xl font-bold tracking-tight whitespace-nowrap ${textColor}`}
          >
            ACBuy<span className="font-light">Spreadsheet</span>
          </Link>
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${textMuted}`}
            >
              Home
            </Link>
            <Link
              to="/acbuyspreadsheet-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${textMuted}`}
            >
              Shopping Guide
            </Link>
            <a
              href="#categories"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${textMuted}`}
            >
              Categories
            </a>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${dropdownBtnBg}`}
            >
              <span>All Categories</span>
              <i
                className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>
            {isOpen && (
              <div className="absolute top-full right-0 mt-2 w-72 max-h-[420px] overflow-y-auto bg-background-50 rounded-lg border border-background-200/70 py-2 z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-base text-foreground-400`}></i>
                    </span>
                    <span className="whitespace-nowrap">{cat.name}</span>
                    <i className="ri-external-link-line text-xs text-foreground-300 ml-auto"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Buy Now
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md ${hamburgerColor}`}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 space-y-1">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-md"
            onClick={() => setMobileOpen(false)}
          >
            <i className="ri-home-line text-base text-foreground-400"></i>
            Home
          </Link>

          <Link
            to="/acbuyspreadsheet-guide"
            className="flex items-center gap-2 px-3 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-md"
            onClick={() => setMobileOpen(false)}
          >
            <i className="ri-book-open-line text-base text-foreground-400"></i>
            Shopping Guide
          </Link>

          <button
            onClick={() => setMobileCatOpen(!mobileCatOpen)}
            className="flex items-center justify-between w-full px-3 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-md"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line text-base text-foreground-400"></i>
              Categories
            </span>
            <i className={`ri-arrow-down-s-line text-base text-foreground-400 transition-transform ${mobileCatOpen ? "rotate-180" : ""}`}></i>
          </button>

          {mobileCatOpen && (
            <div className="pl-9 pr-2 py-1 space-y-0.5 border-l-2 border-background-200 ml-3">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-2 py-2 text-sm text-foreground-600 hover:text-foreground-950 transition-colors rounded"
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs text-foreground-300`}></i>
                  </span>
                  <span className="whitespace-nowrap">{cat.name}</span>
                  <i className="ri-external-link-line text-[10px] text-foreground-300 ml-auto"></i>
                </a>
              ))}
            </div>
          )}

          <Link
            to="/acbuyspreadsheet-guide"
            className="flex items-center gap-2 px-3 py-3 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md"
            onClick={() => setMobileOpen(false)}
          >
            <i className="ri-guide-line text-base text-foreground-400"></i>
            All Guides
          </Link>

          <div className="pt-3 mt-2 border-t border-background-200/70">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-500 text-background-50 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              ACBuy Spreadsheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}